import React from 'react';

// Survey question text is authored by clinic admins through ChiroUp's
// staff app, so the strings can include simple inline HTML for
// emphasis (e.g. `<u>immediate family</u>`). We parse the string with
// the browser's DOMParser and walk only a whitelist of safe inline
// tags, so a hostile `<script>` is rendered as inert text rather than
// executed. `<a>` hrefs are restricted to http/https/mailto/tel.
//
// SSR fallback: if `DOMParser` isn't defined, returns the raw string,
// which is also safe (React escapes it).

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'A', 'P', 'SPAN']);
const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

export const renderSurveyHtml = (html: string): React.ReactNode => {
  if (!html) return null;
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  if (!root) return html;

  const walk = (node: ChildNode, key: number): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const el = node as Element;
    const tag = el.tagName;
    const children = Array.from(el.childNodes).map((c, i) => walk(c, i));

    if (!ALLOWED_TAGS.has(tag)) return <React.Fragment key={key}>{children}</React.Fragment>;
    if (tag === 'BR') return <br key={key} />;

    if (tag === 'A') {
      const href = el.getAttribute('href') || '';

      if (!SAFE_HREF.test(href)) {
        return <React.Fragment key={key}>{children}</React.Fragment>;
      }

      return (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }

    const Tag = tag.toLowerCase() as keyof React.JSX.IntrinsicElements;

    return React.createElement(Tag, { key }, children);
  };

  return Array.from(root.childNodes).map((c, i) => walk(c, i));
};
